import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import ModusIcon from "legacy-components/icons/ModusIcon";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import TeamServices from "pages/home/team/TeamService";
import { TeamListViewer } from "pages/home/teams/TeamListViewer";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTeamStyles } from "./TeamSelector";
import * as actions from "../../store/actions/actions";
import { getTeamDetails, setTeamDetails } from "libs/utils/storage/teamDetailsStorage";
import { useDialogProps } from "legacy-components/dialog/Dialog";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useRole } from "libs/security/authorization/Permission";
import { useHistory, useLocation } from "react-router";
import Loader from "modus/components/misc/Loader";
import { TeamJoiner } from "pages/home/teams/TeamJoiner";

export default function TeamSelectorUserOnBoarding(props: any) {
  const teamClasses = useTeamStyles();
  const dispatch = useDispatch();
  const [rolepermission, userRoleState, changeTeam] = useRole();

  useEffect(() => {
    if (!changeTeam || !dispatch) {
      return;
    }

    const storedTeamId = getTeamDetails(rolepermission?.uuid)?.teamId;
    changeTeam(storedTeamId as string);
    dispatch({
      type: actions.storeTeamDetails,
      payload: { currentTeam: storedTeamId },
    });
  }, [dispatch, changeTeam, rolepermission?.uuid]);

  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [extendedLoading, setExtendedLoading] = useState<boolean>(false);

  const { state, subscribe } = useAsyncAction(() =>
    TeamServices.getUsersTeamByUuid(userRoleState?.value?.uuid as string)
  );

  const { state: accountsState, subscribe: accountsSubscribe } = useAsyncAction(
    () => TeamServices.getUserAccounts(userRoleState?.value?.uuid as string)
  );

  const { open: selectOpen, toggle: selectToggle } = useDialogProps();

  const { open: joinOpen, toggle: joinToggle } = useDialogProps();

  const onClick = () => {
    selectToggle();
    if (!accountsState?.loaded) {
      accountsSubscribe();
    }
  };
  const history = useHistory();

  const handleChangeNew = (team: any) => {
    toggleLoading(!isLoading);
    changeTeam(team?.id as string);
    setTeamDetails(userRoleState?.value?.uuid, {
      teamId: team?.id as string,
      teamName: team?.teamName as string,
    });
    props?.subscribe();

    dispatch({
      type: actions.storeTeamDetails,
      payload: { currentTeam: team },
    });
    setTimeout(() => {
      dispatch(history.push("/home"));
    }, 500);

    return true;
  };

  useEffect(() => {
    if (!toggleLoading || !isLoading) {
      return;
    }

    toggleLoading(!isLoading);
  }, [userRoleState?.value?.teamId, toggleLoading]);

  const [selectedTeamName, setSelectedTeamName] = useState<string>("");

  const [data, setData] = useState<Array<any | null>>([]);

  useEffect(() => {
    if (!data || !props?.state || !state || !setSelectedTeamName) {
      return;
    }

    const stored = getTeamDetails(userRoleState?.value?.uuid);
    const storedTeamKey = stored?.teamId;
    const storedSelectedTeam = stored?.teamName;
    if (storedTeamKey && storedSelectedTeam) {
      // Both values are set properly - use them as default
      setSelectedTeamName(storedSelectedTeam);
    } else if (storedTeamKey && !storedSelectedTeam && data?.length > 0) {
      // Only team key is set - find the name from the data list
      const selectedTeam = data.find(
        (item) => item?.team?.id === storedTeamKey
      );
      if (selectedTeam) {
        const teamName = selectedTeam.team.teamName;
        setSelectedTeamName(teamName);
        setTeamDetails(userRoleState?.value?.uuid, {
          teamId: storedTeamKey,
          teamName,
        });
      }
    } else if (data && data?.length >= 1) {
      // Neither is set - use first team from the list
      setSelectedTeamName(data[0]?.team?.teamName);
      setTeamDetails(userRoleState?.value?.uuid, {
        teamId: data[0]?.team?.id as string,
        teamName: data[0]?.team?.teamName as string,
      });
    } else if (props?.state?.loading && state?.loading) {
      setSelectedTeamName("");
    } else if (props?.state?.loaded && data?.length === 0) {
      setSelectedTeamName("Select a Team");
    }
  }, [data, props, state, setSelectedTeamName, userRoleState?.value?.uuid]);

  useEffect(() => {
    if (!props?.state?.value || !history || !setData) {
      return;
    }

    if (state?.loaded && state?.value) {
      if (
        !state?.error &&
        !props.state.error &&
        state?.value &&
        state?.value?.count === 0
      ) {
        history.push("/guest/home");
      }
      setData(state?.value?.values);
    } else if (
      !state?.error &&
      !props.state.error &&
      props?.state?.value &&
      props?.state?.loaded
    ) {
      if (props?.state?.value?.count === 0) {
        history.push("/guest/home");
      }
      const values = props?.state?.value?.values;
      setData(values);
    }
  }, [history, setData, props?.state, state]);

  const { state: availableTeamsState, subscribe: availableTeamsSubscribe } =
    useAsyncAction(() => TeamServices.getAvailableTeamsForUser());

  const getAvailableTeams = () => {
    availableTeamsSubscribe();
    if (!accountsState?.loaded) {
      accountsSubscribe();
    }
    if (!joinOpen) {
      joinToggle();
    }
  };

  // Effect to manage extended loading state
  useEffect(() => {
    const propsLoading = props?.state?.loading || false;
    const stateLoading = state?.loading || false;

    // When loading begins
    if (propsLoading || isLoading || stateLoading) {
      setExtendedLoading(true);
    }
    // When loading ends
    else if (!propsLoading && !isLoading && !stateLoading) {
      // Keep the loader visible for 4 more seconds after actual loading is done
      const timer = setTimeout(() => {
        setExtendedLoading(false);
      }, 2000); // 2 seconds delay

      return () => {
        clearTimeout(timer); // Cleanup the timer on component unmount or dependencies change
      };
    }
  }, [isLoading, props, state]);

  return (
    <>
      <Box className={teamClasses.container}>
        <Box width="auto">
          {(props?.state?.loading ||
            isLoading ||
            extendedLoading ||
            state?.loading) && (
            <div className="team-selection-loader">
              <Loader showBackDrop={true} />
            </div>
          )}
          {data?.length >= 1 && props?.state?.loaded && (
            <TeamListViewer
              selectOpen={selectOpen}
              selectToggle={selectToggle}
              joinOpen={joinOpen}
              joinToggle={joinToggle}
              data={data}
              setSelectedTeamName={setSelectedTeamName}
              handleChangeNew={handleChangeNew}
              selectedTeamName={selectedTeamName}
              refreshUserTeams={subscribe}
              userUuid={userRoleState?.value?.uuid as string}
              getAvailableTeams={getAvailableTeams}
              availableTeamsState={availableTeamsState}
              newUser={props?.newUser}
              accountsState={accountsState}
            />
          )}
          {data?.length === 0 && props?.state?.loaded && (
            <TeamJoiner
              refreshUserTeams={props?.subscribe}
              userUuid={userRoleState?.value?.uuid as string}
              getAvailableTeams={getAvailableTeams}
              state={availableTeamsState}
              teamList={data}
              open={joinOpen}
              toggle={joinToggle}
              newUser={props?.newUser}
              accountsState={accountsState}
            />
          )}

          {selectedTeamName ? (
            <Button
              variant="outlined"
              color="secondary"
              style={{
                display: "flex",
                alignItems: "center",
                height: "43px",
                width: "auto",
                borderRadius: "0px",
                justifyContent: "space-between", // Adjusted to space-between
                padding: "0px 15px 0px 44px", // Added padding to ensure proper spacing
              }}
              startIcon={
                <ModusIcon
                  name={"people_group"}
                  className={teamClasses.teamIcon}
                  style={{ marginRight: "41px" }}
                />
              }
              endIcon={<ExpandMoreIcon style={{ marginLeft: "20px" }} />}
              onClick={data?.length > 0 ? onClick : getAvailableTeams}
            >
              {selectedTeamName}
            </Button>
          ) : (
            <Box className={teamClasses.initial}>
              <ModusIcon
                name={"people_group"}
                className={teamClasses.initialIcon}
              />
              {props?.state?.loaded && (
                <Typography variant="body2" className="sector">
                  {props.group}
                </Typography>
              )}
              {selectedTeamName === "" && props?.state?.loading && (
                <CircularProgress
                  color="primary"
                  size="20px"
                  style={{ marginLeft: "15px" }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
